import { type Editor, type NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "@btnet/utilities-core";

import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { MentionUserOverlay } from "commonComponents/btWrappers/editor/core/BTEditorContent/overlays/MentionUserOverlay/MentionUserOverlay";
import { fastIsNodeInSelection } from "commonComponents/btWrappers/editor/core/editor.utils";
import {
    type IMentionUserAttributes,
    MentionUserExtensionChar,
    MentionUserTypes,
} from "commonComponents/btWrappers/editor/core/extensions/custom/mentionUser/MentionUserExtension";

import "./MentionUserNodeView.less";

export const MentionUserNodeView = ({
    selected: isSolelySelected,
    editor,
    node,
}: NodeViewProps) => {
    const userInfo = useRequiredContext(UserInfoContext);
    const [isWithinTextSelection, setIsWithinTextSelection] = useState<boolean>(false);
    const [isOptionsOverlayVisible, setIsOptionsOverlayVisible] = useState<boolean>(false);

    const attrs = node.attrs as IMentionUserAttributes;

    // Handle selection updates to highlight the node when it's part of a text selection
    useEffect(() => {
        const onSelectionUpdate = ({
            editor: selectionUpdateEditor,
        }: {
            editor: Editor;
            transaction: Editor["state"]["tr"];
        }) => {
            setIsWithinTextSelection(
                fastIsNodeInSelection(
                    selectionUpdateEditor.state.doc,
                    selectionUpdateEditor.state.selection,
                )(node),
            );
        };

        editor.on("selectionUpdate", onSelectionUpdate);

        return () => {
            editor.off("selectionUpdate", onSelectionUpdate);
        };
    }, [editor, node]);

    // Close options overlay when selection changes away from this node
    useEffect(() => {
        if (!isSolelySelected && isOptionsOverlayVisible) {
            setIsOptionsOverlayVisible(false);
        }
    }, [isSolelySelected, isOptionsOverlayVisible]);

    const handleClick = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            // Don't show the overlay for @everyone mentions
            if (attrs.mentionUserType !== MentionUserTypes.Everyone) {
                setIsOptionsOverlayVisible(!isOptionsOverlayVisible);
            }
        },
        [attrs.mentionUserType, isOptionsOverlayVisible],
    );

    const isHighlighted = isSolelySelected || isWithinTextSelection;

    return (
        <NodeViewWrapper
            as="span"
            className="MentionUserNodeViewWrapper"
            data-drag-handle
            tabIndex={-1}
        >
            <span
                data-testid={`mentionUser-${attrs.id}`}
                className={cn("MentionUserNodeView", {
                    MentionUserNodeViewSelected: isHighlighted,
                    // userInfo can be out of context in some cases
                    MentionUserNodeViewYourself:
                        attrs.mentionUserType === MentionUserTypes.Everyone ||
                        (userInfo && userInfo.globalUserId === attrs.id),
                    MentionUserNodeViewEditable: editor.isEditable,
                })}
                onClick={handleClick}
            >
                <span className="MentionUserNodeViewTextWrapper">
                    <span className="MentionUserNodeViewSymbol">{MentionUserExtensionChar}</span>
                    <span className="MentionUserNodeViewName">{attrs.label}</span>
                </span>
            </span>
            {isOptionsOverlayVisible &&
                createPortal(
                    <MentionUserOverlay
                        editor={editor}
                        nodeAttrs={attrs}
                        onClose={() => setIsOptionsOverlayVisible(false)}
                    />,
                    document.body,
                )}
        </NodeViewWrapper>
    );
};
